import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';

/**
 * AuthContext - Manages authentication state using httpOnly cookies.
 *
 * Security (SOC 2 CC6.1 Compliance):
 * - JWT tokens are stored in httpOnly cookies (set by backend)
 * - Tokens are NOT accessible to JavaScript (XSS protection)
 * - User info is stored in localStorage (not sensitive, just for UI)
 * - CSRF tokens are included automatically by api.ts interceptor
 *
 * The backend sets the following cookies on login:
 * - gus_admin_token: httpOnly JWT token (not accessible to JS)
 * - gus_csrf_token: CSRF token (readable by JS for header)
 */

interface User {
  username: string;
  email: string;
  is_super_admin: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  firebaseLogin: (idToken: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user info is saved (token is in httpOnly cookie, not accessible here)
    // We only store non-sensitive user info in localStorage for UI purposes
    const savedUser = localStorage.getItem('adminUser');

    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        // Invalid JSON in localStorage, clear it
        localStorage.removeItem('adminUser');
      }
    }

    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login({ username, password });

      // Token is now set in httpOnly cookie by backend (not returned in response)
      // We only store user info in localStorage for UI display
      localStorage.setItem('adminUser', JSON.stringify(response.user));

      setUser(response.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Firebase Authentication - primary method for Google Sign-In
  const firebaseLogin = async (idToken: string) => {
    try {
      const response = await authAPI.firebaseLogin(idToken);

      // Token is now set in httpOnly cookie by backend
      localStorage.setItem('adminUser', JSON.stringify(response.user));

      setUser(response.user);
    } catch (error) {
      console.error('Firebase login error:', error);
      throw error;
    }
  };

  // Legacy Google OAuth (deprecated - use firebaseLogin instead)
  const googleLogin = async (credential: string) => {
    try {
      const response = await authAPI.googleLogin(credential);

      // Token is now set in httpOnly cookie by backend
      localStorage.setItem('adminUser', JSON.stringify(response.user));

      setUser(response.user);
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  /**
   * Logout - clears authentication state
   *
   * The backend /auth/logout endpoint clears the httpOnly cookie
   * that we cannot access from JavaScript. We also clear the
   * localStorage user info here.
   */
  const logout = async () => {
    await authAPI.logout();
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        firebaseLogin,
        googleLogin,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
